/* eslint-disable @typescript-eslint/no-floating-promises */
import React from 'react';
import { verifySession } from '@vtblife/otp-api/axios-gen/otp_user_service';

import { useGqlContext } from '@search/gql-client/src';
import { isProfessional } from '@search/auth/src/helpers/isProfessional';
import { useM2AuthContext } from '@search/auth/src/M2AuthProvider';
import { UseAuthError } from '@search/auth/src/useAuth';
import { AuthInfo } from '@search/auth/src/AuthInfo';
import { confirmEmailMutation } from '@search/vtbeco-frontend-core/domain/emails/ConfirmEmailMutation/ConfirmEmailMutation';
import type { ConfirmEmailMutation$variables as ConfirmEmailMutationVariables, ConfirmEmailMutation$data as ConfirmEmailMutationResponse } from '@search/vtbeco-frontend-core/domain/emails/ConfirmEmailMutation/__generated__/ConfirmEmailMutation.graphql';
import { AgentSavingSearchMutation } from '../Agent/hooks/AgentSavingSearchMutation';
import type { AgentSavingSearchMutation$variables, AgentSavingSearchMutation$data } from '../Agent/hooks/__generated__/AgentSavingSearchMutation.graphql';
import type { SavingSearchMutation$variables as SavingSearchMutationVariables, SavingSearchMutation$data as SavingSearchMutationResponse } from './gql/__generated__/SavingSearchMutation.graphql';
import { SavingSearchMutation } from './gql/SavingSearchMutation';

const openOrAuth = (authInfo: AuthInfo) => {
    return new Promise((resolve, reject) => {
        if (authInfo.isAuthenticated) {
            resolve(null);
        } else {
            reject();
        }
    });
};

export type OtpSession = {
    id: string;
    passCodeLength: number;
    retrySendDelayInSec: number;
}

const optInit: OtpSession = {
    id: '',
    passCodeLength: 0,
    retrySendDelayInSec: 0
};

type Params = {
    filters: SavingSearchMutationVariables['filters'];
    headerTitle: string;
    handleClose: () => void;
    openSaveSearch: () => void;
}

type SaveSearchModalVariant = ''
    | 'saveSearch'
    | 'saveSearchSuccess'
    | 'saveSearchError'
    | 'subscriptionSuccess'
    | 'confirmEmail'
;

export const useSaveSearch = ({
    headerTitle,
    handleClose,
    openSaveSearch,
    filters
}: Params) => {
    const [ modalName, setModal ] = React.useState<SaveSearchModalVariant>('');
    const [ searchEmail, setSearchEmail ] = React.useState('');
    const [ searchTitle, setSearchTitle ] = React.useState(headerTitle);
    const [ otpSessionData, setOtpSession ] = React.useState(optInit);
    const gql = useGqlContext();
    const { auth, waitAuth } = useM2AuthContext();
    const isProf = isProfessional(auth.user?.currentRoleType);

    React.useEffect(() => {
        // @ts-expect-error
        waitAuth(authInfo => openOrAuth(authInfo).then(() => {
            setModal('saveSearch');
            openSaveSearch();
        }).catch(() => {
            handleClose();
            throw new UseAuthError();
        }), {
            modalTitle: 'Сохранение поиска',
            modalSubtitle: 'Чтобы сохранить поиск, войдите или зарегистрируйтесь.',
            continueAfterLogin: true
        });
    }, [ waitAuth, gql, setModal ]);

    React.useEffect(() => {
        setSearchTitle(headerTitle);
    }, [ headerTitle ]);

    const saveSearch = React.useCallback(() => {
        gql.execute<SavingSearchMutationVariables, SavingSearchMutationResponse>(
            SavingSearchMutation, {
                title: searchTitle,
                email: searchEmail.length ? searchEmail : undefined,
                filters
            })
            .then(({ data }) => {
                const email = data?.savingSearch.email;

                if (! data?.savingSearch.isSuccess) {
                    return setModal('saveSearchError');
                }

                if (email) {
                    setSearchEmail(email);
                    setModal('subscriptionSuccess');
                } else {
                    setModal('saveSearchSuccess');
                }
            })
            .catch(() => {
                return setModal('saveSearchError');
            })
        ;
    }, [ filters, searchTitle, searchEmail ]);

    const saveSearchForMeAndCustomers = React.useCallback((forMe: boolean, forCustomers: string[]) => {
        const promises: Promise<{ isSuccess: boolean; email?: string | null }>[] = [];

        if (forMe) {
            const forMePromise = gql.execute<SavingSearchMutationVariables, SavingSearchMutationResponse>(
                SavingSearchMutation, {
                    title: searchTitle,
                    email: searchEmail.length ? searchEmail : undefined,
                    filters
                }
            )
                .then(({ data }) => ({
                    isSuccess: data?.savingSearch.isSuccess ?? false,
                    email: data?.savingSearch.email
                }))
                .catch(() => ({ isSuccess: false }));

            promises.push(forMePromise);
        }

        forCustomers.forEach((customerId) => {
            const customerPromise = gql.execute<AgentSavingSearchMutation$variables, AgentSavingSearchMutation$data>(
                AgentSavingSearchMutation, {
                    customerId,
                    title: searchTitle,
                    email: searchEmail.length ? searchEmail : undefined,
                    filters
                }
            )
                .then(({ data }) => ({
                    isSuccess: data?.businessSavingSearch.isSuccess ?? false,
                    email: data?.businessSavingSearch.email
                }))
                .catch(() => ({ isSuccess: false }));

            promises.push(customerPromise);
        });

        Promise.all(promises)
            .then((promisesRes) => {
                if (promisesRes.some(el => el.isSuccess === false)) {
                    return setModal('saveSearchError');
                }

                const resEmail = promisesRes.find(el => el.email);

                if (resEmail?.email) {
                    setSearchEmail(resEmail.email);
                    return setModal('subscriptionSuccess');
                }

                return setModal('saveSearchSuccess');
            });
    }, [ filters, searchTitle, searchEmail ]);

    const confirmEmail = React.useCallback(() => {
        gql.execute<ConfirmEmailMutationVariables, ConfirmEmailMutationResponse>(
            confirmEmailMutation, { email: searchEmail })
            .then(({ data }) => {
                const otpSession = data?.confirmEmail?.otpSession;

                if (otpSession?.id) {
                    setOtpSession(otpSession);
                }

                setModal('confirmEmail');
            });
    }, [ searchEmail ]);

    const resetEmail = React.useCallback(() => {
        setModal('saveSearch');
    }, []);

    const verifyEmail = React.useCallback(async (confirmCode: string) => {
        await verifySession({
            data: {
                code: confirmCode
            },
            urlParams: {
                sessionId: otpSessionData.id
            }
        });

        const confirmResponse = await gql.execute<ConfirmEmailMutationVariables, ConfirmEmailMutationResponse>(
            confirmEmailMutation, {
                sessionId: otpSessionData.id
            });

        if (confirmResponse.data?.confirmEmail?.emailConfirmed) {
            saveSearch();
        }
    }, [ otpSessionData.id ]);

    return {
        modalName,
        searchEmail,
        searchTitle,
        setSearchEmail,
        setSearchTitle,
        saveSearch,
        confirmEmail,
        otpSessionData,
        resetEmail,
        verifyEmail,
        saveSearchForMeAndCustomers,
        isProfessional: isProf
    };
};
